import {
  CHAT_PROVIDERS,
  FLOW_TYPES,
  RTC_PROVIDERS,
  SESSION_MODES,
  SIGNALING_PROVIDERS,
} from '../providerIds'
import {
  createSessionCapabilities,
  createSessionRoutingDecision,
  normalizeFlowType,
  normalizeSessionMode,
} from './sessionRoutingContract'

export const LEGACY_SESSION_PLATFORM_IDS = Object.freeze({
  agora: 'aumhum',
  offline: 'offline',
})

const ONLINE_SESSION_ALIASES = new Set([
  'agora',
  'aumhum',
  'online',
  'video',
  'video-call',
  'video_call',
  'videocall',
])

const OFFLINE_SESSION_ALIASES = new Set([
  'clinic',
  'in-clinic',
  'in-person',
  'in_person',
  'inperson',
  'offline',
])

const HOME_VISIT_SESSION_ALIASES = new Set(['home-visit', 'home_visit', 'homevisit'])

function normalizeLegacyMeetingSrc(meetingSrc) {
  return typeof meetingSrc === 'string' ? meetingSrc.trim().toLowerCase() : null
}

export function normalizeLegacyPlatformId(
  legacyPlatformId,
  fallbackValue = LEGACY_SESSION_PLATFORM_IDS.agora,
) {
  return Object.values(LEGACY_SESSION_PLATFORM_IDS).includes(legacyPlatformId)
    ? legacyPlatformId
    : fallbackValue
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function createLegacyCapabilityDefaults(flowType, sessionMode) {
  if (sessionMode !== SESSION_MODES.online) {
    return createSessionCapabilities()
  }

  switch (flowType) {
    case FLOW_TYPES.consumerLiveSession:
    case FLOW_TYPES.mentorLiveSession:
      return createSessionCapabilities({
        screenshare: true,
        remoteMute: true,
      })
    case FLOW_TYPES.mentorChamberAutoJoin:
      return createSessionCapabilities({
        screenshare: true,
        remoteMute: true,
        chamberScreenControl: true,
      })
    case FLOW_TYPES.chamberScreen:
    case FLOW_TYPES.kioskScreen:
      return createSessionCapabilities({
        remoteMute: true,
        chamberScreenControl: true,
      })
    case FLOW_TYPES.auditSession:
      return createSessionCapabilities({
        remoteMute: true,
        auditObserverJoin: true,
      })
    case FLOW_TYPES.publicLiveSession:
    default:
      return createSessionCapabilities()
  }
}

export function deriveSessionModeFromLegacyFields(sessionData = {}) {
  const explicitSessionMode = normalizeSessionMode(sessionData?.sessionMode, null)
  if (explicitSessionMode) {
    return explicitSessionMode
  }

  const normalizedMeetingSrc = normalizeLegacyMeetingSrc(sessionData?.meetingSrc || sessionData?.mode)

  if (!normalizedMeetingSrc) {
    return SESSION_MODES.online
  }

  if (HOME_VISIT_SESSION_ALIASES.has(normalizedMeetingSrc)) {
    return SESSION_MODES.homeVisit
  }

  if (OFFLINE_SESSION_ALIASES.has(normalizedMeetingSrc)) {
    return SESSION_MODES.offline
  }

  if (ONLINE_SESSION_ALIASES.has(normalizedMeetingSrc)) {
    return SESSION_MODES.online
  }

  return SESSION_MODES.online
}

export function deriveLegacySessionRouting({
  flowType = FLOW_TYPES.consumerLiveSession,
  sessionData = {},
  capabilityOverrides = {},
} = {}) {
  const normalizedFlowType = normalizeFlowType(flowType)
  const sessionMode = deriveSessionModeFromLegacyFields(sessionData)

  return createSessionRoutingDecision({
    flowType: normalizedFlowType,
    sessionMode,
    rtcProvider: RTC_PROVIDERS.agora,
    signalingProvider: SIGNALING_PROVIDERS.agoraRtm,
    chatProvider: CHAT_PROVIDERS.agoraChat,
    resolvedByPolicy: 'legacy-meeting-src-default',
    capabilities: {
      ...createLegacyCapabilityDefaults(normalizedFlowType, sessionMode),
      ...(isPlainObject(capabilityOverrides) ? capabilityOverrides : {}),
    },
  })
}

export function getLegacyPlatformIdFromSessionRouting(sessionRoutingDecision, fallbackMeetingSrc) {
  const normalizedMeetingSrc = normalizeLegacyMeetingSrc(fallbackMeetingSrc)

  if (normalizedMeetingSrc) {
    if (HOME_VISIT_SESSION_ALIASES.has(normalizedMeetingSrc)) {
      return LEGACY_SESSION_PLATFORM_IDS.offline
    }

    if (OFFLINE_SESSION_ALIASES.has(normalizedMeetingSrc)) {
      return LEGACY_SESSION_PLATFORM_IDS.offline
    }

    if (ONLINE_SESSION_ALIASES.has(normalizedMeetingSrc)) {
      return LEGACY_SESSION_PLATFORM_IDS.agora
    }
  }

  const sessionMode = normalizeSessionMode(sessionRoutingDecision?.sessionMode)
  return sessionMode === SESSION_MODES.online
    ? LEGACY_SESSION_PLATFORM_IDS.agora
    : LEGACY_SESSION_PLATFORM_IDS.offline
}
